import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { v4 as getUUID } from 'uuid';

import './_SvgIcon.scss';

/**
 * A svg icon component which relies on svg file that exits in the SVGStore component
 */
export default class SvgIcon extends Component {
  /**
   * Constructor
   */
  constructor(props) {
    super(props);

    this.state = {
      shouldPrependWindowLocation: false
    };
  }

  UNSAFE_componentWillMount() {
    this.externalSvgId = getUUID();
  }

  componentDidMount() {
    // This is a temporary fix for https://git.autodesk.com/BIM360/bim360-matrix-react-components/issues/341.
    // We prepend `window.location.href` if the current browser is `Edge` of `Safari` and `<base />` tag exists.
    // I hope we could remove this code once we have a better solution to handle SVGs & SVGStore when `<base />` tag exists.
    const userAgent = navigator.userAgent.toLowerCase();
    const isEdge = userAgent.indexOf('edge') >= 0;
    const isSafari = userAgent.indexOf('safari') >= 0 && userAgent.indexOf('chrome') === -1;
    if ((isEdge || isSafari) && document.querySelector('base')) {
      this.setState({ shouldPrependWindowLocation: true });
    }
  }

  getSvgHref(svgId) {
    const prefix = this.state.shouldPrependWindowLocation ? window.location.href : '';
    return `${prefix}#${svgId}`;
  }

  /**
   * Renders a svg with a use element
   * @param props
   * @returns {XML}
   */
  renderUseSvgElement(props) {
    const { width, height, href, transform, className, ...restProps } = props;
    return (
      <svg {...restProps} className={className} width={width} height={height}>
        <use xlinkHref={href} transform={transform} href={href} />
      </svg>
    );
  }

  /**
   * Renders an SVG by it's definition as returned by the svg-store-loader.
   * @param svgObject - the svg object ready for SvgStore usage.
   * @param svgProps - the props for the svg.
   * @param transform - the transform to apply on the element.
   * @returns {XML}
   */
  renderExternalSVG(svgObject, svgProps, transform) {
    const href = this.getSvgHref(this.externalSvgId);
    const transformAttribute = transform ? `transform="${transform}" ` : '';
    const svgSymbol = svgObject.symbol.replace(
      /id=([^]*?) /,
      (match, id) => `id="${this.externalSvgId}"`
    );
    const externalSvgDeclaration = `<defs>${svgObject.defs.join(
      ''
    )}</defs>${svgSymbol}<use xlink:href="${href}" href="${href}" ${transformAttribute}></use>`;

    return <svg {...svgProps} dangerouslySetInnerHTML={{ __html: externalSvgDeclaration }} />;
  }

  /**
   * render
   */
  render() {
    const { svgId, className, transform, children, ...otherProps } = this.props;
    const cx = classNames('SvgIcon', className);
    const href = this.getSvgHref(svgId);

    const svgProps = { className: cx, ...otherProps };

    // In case an SVG was inlined
    if (children && typeof children === 'function') {
      const childrenValue = children();
      if (this.state.shouldPrependWindowLocation) {
        childrenValue.defs = (childrenValue.defs || []).map(def =>
          def.replace(/ xlink:href="#/, ' xlink:href="' + window.location.href + '#')
        );

        if (childrenValue.symbol) {
          childrenValue.symbol = childrenValue.symbol
            .replace(/ fill="url\(#/g, ' fill="url(' + window.location.href + '#')
            .replace(/ filter="url\(#/g, ' filter="url(' + window.location.href + '#');
        }
      }

      if (children.SvgStoreTransformed || childrenValue.symbol) {
        return this.renderExternalSVG(childrenValue, svgProps, transform);
      }
    }

    svgProps.className = classNames(svgProps.className, 'SvgIcon--store-icon');

    // Use the SvgStore
    return this.renderUseSvgElement({ href, transform, ...svgProps });
  }
}

SvgIcon.defaultProps = {
  width: '16px',
  height: '16px'
};

SvgIcon.propTypes = {
  /**
   * A function that returns an Svg-like object (containing a `symbol` and `defs`).
   */
  children: PropTypes.func,
  /**
   * The svg width
   */
  width: PropTypes.string,
  /**
   * The svg height
   */
  height: PropTypes.string,
  /**
   * An svg id which corresponds to an svg file in the SVGStore
   */
  svgId: PropTypes.string,
  /**
   * A custom className
   */
  className: PropTypes.string,
  /**
   * An transform value for svg element
   */
  transform: PropTypes.string
};
